import { useMemo, useState } from "react";
import { InteractiveChartDot } from "./InteractiveChartDot";
import styled from "styled-components";
import {
  Area,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DateTime } from "luxon";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { useGetAdminLicenseStatsQuery } from "../../query/adminLicenseStatsQuery";
import { LoadingContainer } from "../../components/LoadingContainer";
import { Card } from "../../components/Card";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useTranslation } from "react-i18next";

const LICENSE_TYPE_COLORS: Record<string, string> = {
  [BillingItemType.PACKAGE]: colors.primary,
  [BillingItemType.ADD_ON]: colors.secondary,
  [BillingItemType.PAYMENT_METHOD]: colors.success,
  [BillingItemType.EXTRA_DEVICE]: colors.info,
};

const ChartCard = styled(Card)`
  padding: ${spacing.x2}px;
  margin-bottom: ${spacing.x3}px;
`;

const ChartTitle = styled.h3`
  margin: 0 0 ${spacing.x2}px 0;
  font-size: ${fontSizes.large}px;
`;

const TooltipContent = styled.div`
  padding: ${spacing.x1}px ${spacing.x2}px;
  background: white;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${fontSizes.small}px;

  & > div + div {
    margin-top: 2px;
  }
`;

interface ActiveLicensesChartProps {
  from: string;
  to: string;
  selectedDate: string;
  onSelectDate: (date: string) => void;
}

export const ActiveLicensesChart = ({
  from,
  to,
  selectedDate,
  onSelectDate,
}: ActiveLicensesChartProps) => {
  const { t } = useTranslation();
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const { data: stats, isLoading } = useGetAdminLicenseStatsQuery(from, to);

  const chartData = useMemo(() => {
    const items = stats ?? [];
    return [...items]
      .reverse()
      .map((s) => {
        const date = DateTime.fromISO(s.date);
        const byType = s.licensesByType ?? {};
        return {
          id: s.date,
          value: s.totalLicenses,
          uniqueStoresCount: s.uniqueStoresCount,
          name: date.toFormat("d MMM"),
          [BillingItemType.PACKAGE]: byType[BillingItemType.PACKAGE] ?? 0,
          [BillingItemType.ADD_ON]: byType[BillingItemType.ADD_ON] ?? 0,
          [BillingItemType.PAYMENT_METHOD]:
            byType[BillingItemType.PAYMENT_METHOD] ?? 0,
          [BillingItemType.EXTRA_DEVICE]:
            byType[BillingItemType.EXTRA_DEVICE] ?? 0,
        };
      });
  }, [stats]);

  if (isLoading && !chartData.length) {
    return (
      <ChartCard>
        <LoadingContainer message={t("statistics.loading")} />
      </ChartCard>
    );
  }

  return (
    <ChartCard>
      <ChartTitle>{t("adminLicenseStats.activeLicensesPerDay")}</ChartTitle>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart
          data={chartData}
          margin={{ top: spacing.x2, right: 50, left: 0, bottom: 0 }}
          onMouseMove={(state) => {
            const id = state?.activePayload?.[0]?.payload?.id as
              | string
              | undefined;
            setHoveredDate(id ?? null);
          }}
          onMouseLeave={() => setHoveredDate(null)}
        >
          <XAxis dataKey="name" tick={{ fontSize: fontSizes.small }} />
          <YAxis
            yAxisId="licenses"
            tick={{ fontSize: fontSizes.small }}
            width={40}
          />
          <YAxis
            yAxisId="stores"
            orientation="right"
            tick={{ fontSize: fontSizes.small }}
            width={40}
          />
          <ReferenceLine
            y={0}
            stroke={colors.border}
            strokeWidth={1}
            yAxisId="licenses"
          />
          <Legend
            formatter={(value) =>
              value === "uniqueStoresCount"
                ? t("adminLicenseStats.uniqueStores")
                : t("billingItems.type." + value)
            }
            wrapperStyle={{ fontSize: fontSizes.small }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const p = payload[0].payload;
              const licenseTypes = [
                BillingItemType.PACKAGE,
                BillingItemType.ADD_ON,
                BillingItemType.PAYMENT_METHOD,
                BillingItemType.EXTRA_DEVICE,
              ];
              return (
                <TooltipContent>
                  <div>{p.name}</div>
                  <div>{p.value} licenses</div>
                  {licenseTypes
                    .filter((type) => (p[type] ?? 0) > 0)
                    .map((type) => (
                      <div key={type} style={{ color: LICENSE_TYPE_COLORS[type] }}>
                        {t("billingItems.type." + type)}: {p[type]}
                      </div>
                    ))}
                  {p.uniqueStoresCount != null && (
                    <div>{p.uniqueStoresCount} stores</div>
                  )}
                </TooltipContent>
              );
            }}
            cursor={{ stroke: colors.border, strokeWidth: 1 }}
          />
          <Area
            type="monotone"
            dataKey={BillingItemType.PACKAGE}
            stackId="licenses"
            yAxisId="licenses"
            fill={LICENSE_TYPE_COLORS[BillingItemType.PACKAGE]}
            stroke={LICENSE_TYPE_COLORS[BillingItemType.PACKAGE]}
          />
          <Area
            type="monotone"
            dataKey={BillingItemType.ADD_ON}
            stackId="licenses"
            yAxisId="licenses"
            fill={LICENSE_TYPE_COLORS[BillingItemType.ADD_ON]}
            stroke={LICENSE_TYPE_COLORS[BillingItemType.ADD_ON]}
          />
          <Area
            type="monotone"
            dataKey={BillingItemType.PAYMENT_METHOD}
            stackId="licenses"
            yAxisId="licenses"
            fill={LICENSE_TYPE_COLORS[BillingItemType.PAYMENT_METHOD]}
            stroke={LICENSE_TYPE_COLORS[BillingItemType.PAYMENT_METHOD]}
          />
          <Area
            type="monotone"
            dataKey={BillingItemType.EXTRA_DEVICE}
            stackId="licenses"
            yAxisId="licenses"
            fill={LICENSE_TYPE_COLORS[BillingItemType.EXTRA_DEVICE]}
            stroke={LICENSE_TYPE_COLORS[BillingItemType.EXTRA_DEVICE]}
          />
          <Line
            type="monotone"
            dataKey="uniqueStoresCount"
            yAxisId="stores"
            stroke={colors.warning}
            strokeWidth={2}
            dot={(props) => (
              <InteractiveChartDot
                {...props}
                hoveredDate={hoveredDate}
                selectedDate={selectedDate}
                onSelectDate={onSelectDate}
                fill={colors.warning}
                selectedFill={colors.warning}
              />
            )}
            activeDot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};
